import { defineStore } from "pinia";
import { faker } from '@faker-js/faker';

import {
  DISCOUNT_TYPE_FIXED,
  ORDER_TYPE_DINE_IN,
  ORDER_TYPE_TAKE_OUT,
  PAYMENT_METHOD_CASH,
  PAYMENT_METHOD_CARD,
  PAYMENT_METHOD_PROMPTPAY,
  STATUS_CANCELED,
  STATUS_COMPLETED,
  STATUS_FAILED,
  STATUS_PENDING,
  STATUS_PREPARING,
  STATUS_PROCESSING,
  STATUS_READY,
  STATUS_SERVED
} from "@/constants/order";

export const useOrderStore = defineStore("order", {
  state: () => ({
    cashReceived: 0,
    change: 0,
    customerName: faker.person.fullName(),
    discount: {
      discountType: DISCOUNT_TYPE_FIXED,
      value: 0
    },
    items: [],
    messages: [],
    orderId: null,
    orderType: ORDER_TYPE_DINE_IN,
    orderStatus: STATUS_PENDING,
    paymentMethod: PAYMENT_METHOD_CASH,
    table: null,
    totalAmount: 0,
    totalItems: 0,
    transactionStatus: STATUS_PENDING,
  }),
  actions: {
    getCashReceived() {
      return this.cashReceived;
    },
    getChange() {
      return this.change;
    },
    getCurrentOrderState() {
      return this.$state;
    },
    getCurrentTable() {
      return this.table;
    },
    getCustomerName() {
      return this.customerName;
    },
    getDiscount() {
      return this.discount;
    },
    getOrderId() {
      return this.orderId;
    },
    getOrderItems() {
      return this.items;
    },
    getMessages() {
      return this.messages;
    },
    getOrderType() {
      return this.orderType;
    },
    getOrderStatus() {
      return this.orderStatus;
    },
    getPaymentMethod() {
      return this.paymentMethod;
    },
    getTotalAmount() {
      return this.totalAmount;
    },
    getTotalItems() {
      return this.totalItems;
    },
    getTransactionStatus() {
      return this.transactionStatus;
    },
    updateCashReceived(amount) {
      this.cashReceived = amount;
    },
    updateChange(change) {
      this.change = change;
    },
    updateCurrentTable(table) {
      this.table = table;
    },
    updateCustomerName(name) {
      this.customerName = name;
    },
    updateDiscount(type, value) {
      const discountType = type.toLowerCase();
      this.discount = { discountType, value };
    },
    updateMessages(newMessages) {
      this.messages = {
        ...this.messages,
        ...newMessages
      };
    },
    updateOrderId(id) {
      this.orderId = id;
    },
    updateOrderItems(newItems) {
      this.items = newItems;
      this.totalAmount = this.items.reduce(
        (sum, i) => sum + i.price,
        0
      );

      this.totalItems = this.items.reduce((sum, i) => sum + i.quantity, 0);
    },
    updateOrderStatus(status) {
      const val = status.toLowerCase();
      if ([STATUS_PENDING, STATUS_PROCESSING, STATUS_PREPARING, STATUS_READY, STATUS_SERVED, STATUS_CANCELED].includes(val)) {
        this.orderStatus = val;
      }
    },
    updateOrderType(type) {
      const val = type.toLowerCase();
      if ([ORDER_TYPE_DINE_IN, ORDER_TYPE_TAKE_OUT].includes(val)) {
        this.orderType = val;
      }
    },
    updatePaymentMethod(method) {
      const val = method.toLowerCase();
      if ([PAYMENT_METHOD_CASH, PAYMENT_METHOD_PROMPTPAY, PAYMENT_METHOD_CARD].includes(val)) {
        this.paymentMethod = val;
      }
    },
    updateTransactionStatus(status) {
      const val = status.toLowerCase();
      if ([STATUS_PENDING, STATUS_PROCESSING, STATUS_FAILED, STATUS_COMPLETED].includes(val)) {
        this.transactionStatus = val;
      }
    },
    resetOrder() {
      this.orderId = null;
      this.cashReceived = 0;
      this.change = 0;
      this.customerName = faker.person.fullName();
      this.discount = { discountType: DISCOUNT_TYPE_FIXED, value: 0 };
      this.items = [];
      this.messages = [];
      this.orderStatus = STATUS_PENDING;
      this.orderType = ORDER_TYPE_DINE_IN;
      this.paymentMethod = PAYMENT_METHOD_CASH;
      this.table = null;
      this.totalAmount = 0;
      this.totalItems = 0;
      this.transactionStatus = STATUS_PENDING;
    },
  },
  persist: {
    storage: localStorage,
    paths: [
      "cashReceived",
      "change",
      "customerName",
      "discount",
      "items",
      "messages",
      "orderId",
      "orderStatus",
      "orderType",
      "paymentMethod",
      "resetOrder",
      "table",
      "totalAmount",
      "totalItems",
      "transactionStatus",
    ],
  },
});